<template>
    <div class="first-task-reward-overlay" data-testid="first-task-reward-overlay">
        <section
            class="first-task-reward-prompt"
            role="dialog"
            aria-modal="true"
            aria-labelledby="first-task-reward-title"
            data-testid="first-task-reward-prompt"
        >
            <button
                type="button"
                class="first-task-reward-prompt__close"
                :aria-label="closeLabel"
                data-testid="first-task-reward-close"
                @click="emit('dismiss')"
            >
                <PtIcon icon="SolidCloseMedium" class="first-task-reward-prompt__close-icon" />
            </button>
            <img
                class="first-task-reward-prompt__gift"
                :src="giftImage"
                alt=""
                aria-hidden="true"
            >
            <h2 id="first-task-reward-title">
                {{ title }}
            </h2>
            <p class="first-task-reward-prompt__benefit">
                {{ benefitPrefix }} <strong>{{ credits }}</strong> {{ benefitSuffix }}
            </p>
            <div class="first-task-reward-prompt__actions">
                <button
                    type="button"
                    data-testid="first-task-reward-export"
                    :aria-busy="loadingAction === 'EXPORT'"
                    :disabled="Boolean(loadingAction)"
                    @click="emit('action', 'EXPORT')"
                >
                    <span
                        v-if="loadingAction === 'EXPORT'"
                        class="th-loading th-loading-spinner th-loading-sm"
                        data-testid="first-task-reward-export-loading"
                        aria-hidden="true"
                    />
                    {{ exportNow }}
                </button>
                <button
                    type="button"
                    class="first-task-reward-prompt__secondary"
                    data-testid="first-task-reward-share"
                    :disabled="Boolean(loadingAction)"
                    @click="emit('action', 'SHARE')"
                >
                    {{ shareNow }}
                </button>
            </div>
            <p class="first-task-reward-prompt__validity">
                {{ validity }}
            </p>
        </section>
    </div>
</template>

<script setup lang="ts">
import { PtIcon } from 'patsnap-biz';
import '@patsnap-ui/icon/assets/solid/CloseMedium.svg';
import giftImage from '../assets/first-task-reward-gift.png';
import type { FirstTaskRewardAction } from '../first-task-reward-api';
import type { FirstTaskRewardPromptCopy } from '../first-task-reward-presenter';

/** 权益卡片展示属性。 */
interface FirstTaskRewardPromptProps extends FirstTaskRewardPromptCopy {
    /** 当前正在等待业务成功的动作。 */
    loadingAction?: FirstTaskRewardAction;
}

defineProps<FirstTaskRewardPromptProps>();

const emit = defineEmits<{
    /** 用户选择导出或分享。 */
    (event: 'action', action: FirstTaskRewardAction): void;
    /** 用户通过关闭按钮或 Escape 退出。 */
    (event: 'dismiss'): void;
}>();
</script>

<style lang="scss">
.first-task-reward-overlay {
    position: fixed;
    inset: 0;
    z-index: 3200;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(13, 24, 43, 0.56);
}

.first-task-reward-prompt {
    position: relative;
    width: min(640px, calc(100vw - 40px));
    padding: 226px 72px 26px;
    overflow: hidden;
    color: #14234b;
    text-align: center;
    background:
        radial-gradient(circle at 50% 0, rgba(152, 144, 255, 0.24), transparent 44%),
        #fff;
    border: 1px solid #8ea9e9;
    border-radius: 16px;
    box-shadow: 0 18px 48px rgba(2, 20, 54, 0.24);

    &__close {
        position: absolute;
        top: 13px;
        right: 14px;
        z-index: 2;
        display: flex;
        width: 40px;
        height: 40px;
        align-items: center;
        justify-content: center;
        padding: 0;
        color: #14234b;
        cursor: pointer;
        background: transparent;
        border: 0;
        border-radius: 6px;

        &:hover {
            background: rgba(7, 100, 233, 0.08);
        }
    }

    &__close-icon {
        width: 28px;
        height: 28px;
    }

    &__gift {
        position: absolute;
        top: 0;
        left: calc(50% + 8px);
        z-index: 0;
        width: 400px;
        max-width: calc(100% - 32px);
        height: auto;
        object-fit: contain;
        pointer-events: none;
        transform: translateX(-50%);
        mask-image: linear-gradient(to right, transparent, #000 8%, #000 92%, transparent);
    }

    h2,
    &__benefit,
    &__actions,
    &__validity {
        position: relative;
        z-index: 1;
    }

    h2 {
        margin: 0;
        font-size: 32px;
        font-weight: 700;
        line-height: 40px;
    }

    &__benefit {
        margin: 7px 0 0;
        font-size: 16px;
        line-height: 24px;
    }

    &__benefit {
        margin-top: 2px;

        strong {
            padding: 2px 8px;
            color: #075ee8;
            font-size: 18px;
            font-weight: 500;
            background: #e6f0ff;
            border-radius: 4px;
        }
    }

    &__actions {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
        width: min(100%, 414px);
        margin: 20px auto 0;

        button {
            display: inline-flex;
            min-height: 46px;
            align-items: center;
            justify-content: center;
            gap: 8px;
            color: #fff;
            font-size: 18px;
            cursor: pointer;
            background: #0666ed;
            border: 1px solid #0666ed;
            border-radius: 8px;

            &:disabled {
                color: #fff;
                cursor: not-allowed;
                background: #aeb8c8;
                border-color: #aeb8c8;
            }
        }
    }

    &__actions &__secondary {
        color: #0666ed;
        background: #fff;

        &:disabled {
            color: #9ba5b4;
            background: #f4f6f8;
            border-color: #cbd1dc;
        }
    }

    &__validity {
        margin: 20px 0 0;
        color: #495973;
        font-size: 14px;
        line-height: 20px;
    }
}

@media (max-width: 640px) {
    .first-task-reward-prompt {
        padding: 174px 24px 24px;

        h2 {
            font-size: 26px;
            line-height: 34px;
        }

        &__actions {
            grid-template-columns: 1fr;
        }
    }
}

@media (prefers-reduced-motion: no-preference) {
    .first-task-reward-overlay {
        animation: first-task-reward-fade-in 180ms ease-out;
    }

    .first-task-reward-prompt {
        animation: first-task-reward-rise-in 260ms cubic-bezier(0.22, 1, 0.36, 1);
    }
}

@keyframes first-task-reward-fade-in {
    from { opacity: 0; }
}

@keyframes first-task-reward-rise-in {
    from { opacity: 0; transform: translateY(12px) scale(0.98); }
}
</style>
